import { setToken } from 'app/api/api';
import { makeAutoObservable, reaction, runInAction } from 'mobx';
import { RootStore } from 'app/store/rootStore';
import { authApi } from 'app/api/authApi';
import axios, { AxiosError } from 'axios';
import { profileApi } from 'entities/profile/api/profileApi';
import { ProfileModel } from 'entities/profile/model/userModel';

export enum Role {
  ADMIN = 'ADMIN',
  EDITOR = 'EDITOR',
  VIEWER = 'VIEWER',
}

export enum InviteStatus {
  OK = 'OK',
  IN_WHITE_LIST = 'InWhiteList',
  EXISTS = 'Request exists',
  EXISTS_IN_WHITE_LIST = 'Request exists and in white list',
}

export const ACCESS_TIME = 1000 * 60 * 60 * 48;
export type AuthData = { username: string; password: string };

export type SignupData = {
  password: string;
  code: string;
};

export type InviteData = {
  name: string;
  surname: string;
  email: string;
};

export type RecoverData = {
  password: string;
  code: string;
};

export type SignInError = 'auth';
export type RestoreError = 'wrongPassword';

type Errors = {
  signin: SignInError | null;
  restore: RestoreError | null;
};

export class AuthStore {
  private _rootStore: RootStore;
  private _isSomePending: boolean = false;
  private _user: ProfileModel | null = null;
  public errors: Errors = {
    signin: null,
    restore: null,
  };
  private _rememberMe = true;

  private _signupEmailSend: string | null = null;

  private _signupUserData: InviteData | null = null;

  public get signupEmailSend() {
    return this._signupEmailSend;
  }

  private _isPasswordLinkSend = false;
  public get isPasswordLinkSend() {
    return this._isPasswordLinkSend;
  }

  public get signupUserData() {
    return this._signupUserData;
  }

  private get _storage() {
    return this.rememberMe ? localStorage : sessionStorage;
  }

  public get rememberMe() {
    return this._rememberMe;
  }
  public set rememberMe(val: boolean) {
    if (val) {
      localStorage.setItem('rememberMe', 'y');
    } else {
      localStorage.removeItem('rememberMe');
    }

    this._rememberMe = val;
  }

  private _accessToken = this._storage.getItem('accessToken');
  private _time = this._storage.getItem('authTime');
  private _logoutTrigger = 1;

  private _update() {
    this._logoutTrigger = Math.random();
    this._accessToken = this._storage.getItem('accessToken');
    this._time = this._storage.getItem('authTime');

    if (this._accessToken) {
      setToken(this._accessToken);
    }
  }

  private get _isValidAccessToken() {
    return Boolean(
      this._accessToken &&
        Date.now() - Number(this._time) < ACCESS_TIME &&
        this._logoutTrigger
    );
  }

  constructor({ rootStore }: { rootStore: RootStore }) {
    this._rootStore = rootStore;
    this._update();
    makeAutoObservable(this);

    reaction(
      () => this._isSomePending,
      (isPending) => {
        this._rootStore.stateStore.isAuthStoreLoading = isPending;
      }
    );

    setInterval(() => {
      this._update();
    }, 500);
  }

  public get isAuth() {
    return this._isValidAccessToken;
  }

  public get user() {
    return this._user;
  }

  public clearError() {
    this.errors = {
      signin: null,
      restore: null,
    };
  }

  public logout() {
    localStorage.removeItem('accessToken');
    localStorage.removeItem('authTime');
    sessionStorage.removeItem('accessToken');
    sessionStorage.removeItem('authTime');
    this._update();
  }

  public async sendSignupLink(data: InviteData) {
    this._isSomePending = true;

    try {
      const result = await authApi.sendSignupLink(data);
      this._isSomePending = false;
      this._signupEmailSend = result;
      this._signupUserData = data;
    } catch (e) {
      this._isSomePending = false;
      throw e;
    }
  }

  public async sendRestoreLink(data: { email: string }) {
    this._isSomePending = true;

    try {
      await authApi.sendRestoreLink(data);
      this._isSomePending = false;
      this._isPasswordLinkSend = true;
    } catch (e) {
      this._isSomePending = false;
      throw e;
    }
  }

  public forceLogin(access_token: string) {
    setToken(access_token);
    runInAction(() => {
      this._storage.setItem('accessToken', access_token);
      this._storage.setItem('authTime', JSON.stringify(Date.now()));
      this._update();
    });
  }

  public async login(data: AuthData) {
    this._isSomePending = true;
    this.logout();

    try {
      const result = await authApi.login(data);
      this._isSomePending = false;

      if (result) {
        this.forceLogin(result.access_token);
      }
    } catch (e) {
      this._isSomePending = false;
      if (axios.isAxiosError(e) && e.response?.status === 401) {
        this.errors.signin = 'auth';
        return;
      }

      throw e;
    }
  }

  public async signup(data: SignupData) {
    this._isSomePending = true;

    try {
      const result = await authApi.signup(data);

      this._isSomePending = false;

      return result;
    } catch (e) {
      this._isSomePending = false;
      throw e;
    }
  }

  public async recover(data: RecoverData) {
    this._isSomePending = true;

    try {
      const result = await authApi.recover(data);

      this._isSomePending = false;

      return result;
    } catch (e) {
      this._isSomePending = false;
      throw e;
    }
  }

  public async getMe() {
    this._isSomePending = true;

    try {
      const user = await profileApi.getMe();
      this._user = new ProfileModel(user);
      this._isSomePending = false;
    } catch (e) {
      const err = e as AxiosError;

      if (err.response?.status === 401) {
        this.logout();
      }
      this._isSomePending = false;
      throw e;
    }
  }
}
